import React, { useState, useEffect } from 'react';
import { 
  X, MoreVertical, Calendar, Clock, PoundSterling, 
  MapPin, Phone, Mail, User, CheckCircle2, XCircle,
  SkipForward, RefreshCw, Trash2, Edit3, ChevronRight,
  FileText, MessageSquare, CreditCard, Navigation, Loader2,
  RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date nicely
const formatDateNice = (dateStr) => {
  if (!dateStr) return 'Not set';
  try {
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  } catch {
    return dateStr;
  }
};

const JobDetailModal = ({ 
  job, 
  onClose, 
  onOpenCustomer,
  onComplete,
  onSkip,
  onUnskip,
  onReschedule,
  onDelete,
  onSave
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({
    price: job?.price || 0,
    service: job?.service || job?.services || '',
    servicePrices: job?.service_prices || '',
    notes: job?.notes || '',
    invoice_notes: job?.invoice_notes || '',
    scheduled_date: job?.scheduled_date || job?.scheduledDate || job?.date || '',
    frequency: job?.frequency || '',
    duration: job?.duration || '00:30',
    preferred_time: job?.preferred_time || job?.time || '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Frequency options
  const frequencyOptions = [
    'One-off',
    'Every week',
    'Every 2 weeks',
    'Every 3 weeks',
    'Every 4 weeks',
    'Every 5 weeks',
    'Every 6 weeks',
    'Every 7 weeks',
    'Every 8 weeks',
    'Every 10 weeks',
    'Every 12 weeks',
    'Every 3 months',
    'Every 6 months',
    'Yearly',
  ];

  // Fetch services when edit form opens
  useEffect(() => {
    if (showEditForm) {
      const fetchServices = async () => {
        setLoadingServices(true);
        try {
          const token = localStorage.getItem('streakfree_token');
          const res = await fetch(`${API_URL}/api/services`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setServices(data);
          }
        } catch (err) {
          console.error('Error fetching services:', err);
        } finally {
          setLoadingServices(false);
        }
      };
      fetchServices();
    }
  }, [showEditForm]);

  if (!job) return null;

  const statusConfig = {
    scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Calendar },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
    skipped: { label: 'Skipped', color: 'bg-yellow-100 text-yellow-700', icon: SkipForward },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
    pending: { label: 'Pending', color: 'bg-orange-100 text-orange-700', icon: Clock },
  };

  const status = statusConfig[job.status] || statusConfig.scheduled;
  const StatusIcon = status.icon;

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Calculate total price from individual service prices
      const totalPrice = (editData.servicePrices || '').split(',').reduce((sum, p) => sum + (parseFloat(p) || 0), 0);
      
      // For completed jobs, only update price and related invoice
      const isCompleted = job.status === 'completed';
      
      const response = await fetch(`${API_URL}/api/jobs/${job.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editData,
          price: totalPrice,
          service_prices: editData.servicePrices,
          update_invoice: isCompleted // Tell backend to update invoice if job is completed
        })
      });
      
      if (response.ok) {
        const updatedJob = await response.json();
        setShowEditForm(false);
        // Notify parent component about the update so it can refresh data
        if (onSave) {
          onSave(updatedJob);
        }
        onClose();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to update job');
      }
    } catch (err) {
      console.error('Error updating job:', err);
      alert('Error updating job');
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      if (onComplete) {
        await onComplete(job);
      }
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUndoComplete = async () => {
    if (isProcessing) return;
    if (!window.confirm('Are you sure you want to undo this completion? This will delete the associated invoice (if unpaid) and remove the charge from the customer balance.')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}/undo-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        onClose();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to undo completion');
      }
    } catch (err) {
      console.error('Error undoing completion:', err);
      alert('Error undoing completion');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = async () => {
    if (isProcessing) return;
    const reason = prompt('Reason for skipping this job:');
    if (!reason) return;
    
    setIsProcessing(true);
    try {
      if (onSkip) {
        await onSkip(job, reason);
      }
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl modal-shell-h overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
        
        {/* Header */}
        <div className="bg-zinc-800 text-white p-4">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <X size={22} />
            </button>
            
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <MoreVertical size={20} />
              </button>
              
              {/* Dropdown Menu */}
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-2xl z-50 overflow-hidden border border-zinc-200">
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowEditForm(true);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                    >
                      <Edit3 size={18} className="text-zinc-400" />
                      <span>Edit Job</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        if (onReschedule) onReschedule(job);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                    >
                      <RefreshCw size={18} className="text-zinc-400" />
                      <span>Reschedule</span>
                    </button>
                    
                    {/* Unskip option - only shows for skipped jobs */}
                    {job.status === 'skipped' && onUnskip && (
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onUnskip(job);
                          onClose();
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <RotateCcw size={18} />
                        <span>Unskip Job</span>
                      </button>
                    )}
                    
                    <div className="h-px bg-zinc-200 my-1" />
                    
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        if (window.confirm('Are you sure you want to delete this job?')) {
                          if (onDelete) onDelete(job);
                          onClose();
                        }
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={18} />
                      <span>Delete Job</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Job Info */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
              <FileText size={24} />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold">{job.service || job.services || 'Window Clean'}</h2>
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                <StatusIcon size={12} />
                {status.label}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">£{(job.price || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        {/* Edit Form Modal */}
        {showEditForm && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
              <div className="p-6 border-b border-zinc-200">
                <h3 className="text-lg font-semibold text-zinc-900">Edit Job</h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Warning for completed jobs */}
                {job.status === 'completed' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
                    <strong>Note:</strong> This job is completed. You can only edit pricing to correct the invoice.
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Services</label>
                  {loadingServices ? (
                    <div className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 flex items-center gap-2 text-zinc-400">
                      <Loader2 size={16} className="animate-spin" />
                      Loading services...
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Selected services with individual prices */}
                      {(editData.service || '').split(',').filter(s => s.trim()).map((svc, idx) => {
                        const prices = (editData.servicePrices || '').split(',');
                        const price = prices[idx] || '';
                        return (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg border border-zinc-200">
                            <span className="flex-1 text-sm font-medium text-zinc-700">{svc.trim()}</span>
                            <div className="flex items-center gap-1">
                              <span className="text-zinc-400">£</span>
                              <input
                                type="number"
                                step="0.01"
                                value={price}
                                onChange={(e) => {
                                  const newPrices = [...prices];
                                  newPrices[idx] = e.target.value;
                                  setEditData({...editData, servicePrices: newPrices.join(',')});
                                }}
                                className="w-20 px-2 py-1 rounded border border-zinc-300 text-sm text-right"
                                placeholder="0.00"
                              />
                            </div>
                            {/* Only show remove button if not completed */}
                            {job.status !== 'completed' && (
                              <button
                                type="button"
                                onClick={() => {
                                  const currentServices = (editData.service || '').split(',').filter(s => s.trim());
                                  const currentPrices = (editData.servicePrices || '').split(',');
                                  currentServices.splice(idx, 1);
                                  currentPrices.splice(idx, 1);
                                  setEditData({
                                    ...editData, 
                                    service: currentServices.join(', '),
                                    servicePrices: currentPrices.join(',')
                                  });
                                }}
                                className="p-1 text-zinc-400 hover:text-red-500"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Total */}
                      {(editData.service || '').trim() && (
                        <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                          <span className="font-semibold text-emerald-700">Total</span>
                          <span className="font-bold text-emerald-700">
                            £{(editData.servicePrices || '').split(',').reduce((sum, p) => sum + (parseFloat(p) || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                      
                      {/* Service dropdown to add - only for non-completed jobs */}
                      {job.status !== 'completed' && (
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              const currentServices = (editData.service || '').split(',').filter(s => s.trim());
                              const currentPrices = (editData.servicePrices || '').split(',').filter((p, i) => currentServices[i]);
                              if (!currentServices.includes(e.target.value)) {
                                currentServices.push(e.target.value);
                                currentPrices.push(''); // Empty price for user to fill
                                setEditData({
                                  ...editData, 
                                  service: currentServices.join(', '),
                                  servicePrices: currentPrices.join(',')
                                });
                              }
                            }
                          }}
                          className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none bg-white"
                          data-testid="edit-job-service-dropdown"
                        >
                          <option value="">+ Add a service</option>
                          {services.map(service => (
                            <option key={service.id} value={service.name}>
                              {service.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Date - only editable for non-completed jobs */}
                {job.status !== 'completed' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={editData.scheduled_date?.split('T')[0] || ''}
                      onChange={(e) => setEditData({...editData, scheduled_date: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                    />
                  </div>
                )}
                
                {/* Frequency - only editable for non-completed jobs */}
                {job.status !== 'completed' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Frequency</label>
                    <select
                      value={editData.frequency}
                      onChange={(e) => setEditData({...editData, frequency: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none bg-white"
                    >
                      <option value="">Select frequency...</option>
                      {frequencyOptions.map(freq => (
                        <option key={freq} value={freq}>{freq}</option>
                      ))}
                    </select>
                    {editData.frequency && editData.frequency !== 'One-off' && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Next job will be auto-scheduled based on this frequency
                      </p>
                    )}
                  </div>
                )}
                
                {/* Time & Duration - only editable for non-completed jobs */}
                {job.status !== 'completed' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">Time</label>
                      <input
                        type="time"
                        value={editData.preferred_time || ''}
                        onChange={(e) => setEditData({...editData, preferred_time: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">Duration</label>
                      <select
                        value={editData.duration}
                        onChange={(e) => setEditData({...editData, duration: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none bg-white"
                      >
                        <option value="00:15">15 mins</option>
                        <option value="00:30">30 mins</option>
                        <option value="00:45">45 mins</option>
                        <option value="01:00">1 hour</option>
                        <option value="01:30">1.5 hours</option>
                        <option value="02:00">2 hours</option>
                        <option value="02:30">2.5 hours</option>
                        <option value="03:00">3 hours</option>
                      </select>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Notes</label>
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({...editData, notes: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                    placeholder="Add notes about this job..."
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
                    Notes for invoice
                    <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Customer-facing</span>
                  </label>
                  <textarea
                    value={editData.invoice_notes}
                    onChange={(e) => setEditData({...editData, invoice_notes: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                    placeholder="e.g. Cleaned inside conservatory glass · Next visit due 6 weeks"
                    data-testid="jobdetail-invoice-notes-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Will appear on the invoice the customer receives.</p>
                </div>
              </div>
              <div className="p-6 border-t border-zinc-200 flex gap-3">
                <button
                  onClick={() => setShowEditForm(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Customer Section - Clickable - Navigates to full profile */}
          <button
            onClick={() => onOpenCustomer && onOpenCustomer(job)}
            className="w-full p-4 border-b border-zinc-100 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left"
            data-testid="job-modal-customer-link"
          >
            <div className="w-10 h-10 bg-zinc-200 rounded-full flex items-center justify-center text-zinc-600 font-bold">
              {(job.customerName || job.customer_name || 'U').charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-zinc-900 truncate">{job.customerName || job.customer_name || 'Unknown Customer'}</p>
              <p className="text-sm text-zinc-500 truncate">{job.address || job.customer_address || 'No address'}</p>
              <p className="text-xs text-emerald-600 font-medium mt-1">View Full Profile →</p>
            </div>
            <ChevronRight size={20} className="text-emerald-500" />
          </button>
          
          {/* Job Details */}
          <div className="p-4 space-y-4">
            {/* Date */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Scheduled Date</p>
                <p className="font-medium text-zinc-900">
                  {formatDateNice(job.scheduled_date || job.scheduledDate || job.date)}
                </p>
              </div>
            </div>
            
            {/* Location */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <MapPin size={20} className="text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-zinc-500">Location</p>
                <p className="font-medium text-zinc-900">{job.address || 'No address'}</p>
              </div>
              {job.address && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-100 rounded-xl text-emerald-600 hover:bg-emerald-200 transition-colors"
                >
                  <Navigation size={18} />
                </a>
              )}
            </div>
            
            {/* Zone */}
            {job.zone && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MapPin size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Round/Zone</p>
                  <p className="font-medium text-zinc-900">{job.zone}</p>
                </div>
              </div>
            )}
            
            {/* Notes */}
            {job.notes && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Notes</p>
                  <p className="font-medium text-zinc-900">{job.notes}</p>
                </div>
              </div>
            )}
            
            {/* Invoice notes — customer-facing, rendered on the invoice */}
            {job.invoice_notes && (
              <div className="flex items-start gap-3" data-testid="jobdetail-invoice-notes-display">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText size={20} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Notes for invoice <span className="text-[10px] uppercase tracking-wider text-amber-600">customer-facing</span></p>
                  <p className="font-medium text-zinc-900">{job.invoice_notes}</p>
                </div>
              </div>
            )}
            
            {/* Quick Contact */}
            <div className="flex gap-3 pt-2">
              {job.customerPhone && (
                <a
                  href={`tel:${job.customerPhone}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-100 rounded-xl text-zinc-700 hover:bg-zinc-200 transition-colors"
                >
                  <Phone size={18} />
                  <span className="font-medium">Call</span>
                </a>
              )}
              {job.customerEmail && (
                <a
                  href={`mailto:${job.customerEmail}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-100 rounded-xl text-zinc-700 hover:bg-zinc-200 transition-colors"
                >
                  <Mail size={18} />
                  <span className="font-medium">Email</span>
                </a>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer Actions */}
        {job.status !== 'completed' && job.status !== 'skipped' && (
          <div className="p-4 border-t border-zinc-200 flex gap-3">
            <button
              onClick={handleSkip}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-100 text-amber-700 rounded-xl font-medium hover:bg-amber-200 transition-colors disabled:opacity-50"
            >
              <SkipForward size={18} />
              Skip
            </button>
            <button
              onClick={handleComplete}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 size={18} />
              {isProcessing ? 'Processing...' : 'Complete'}
            </button>
          </div>
        )}
        
        {/* Undo Complete - only for completed jobs */}
        {job.status === 'completed' && (
          <div className="p-4 border-t border-zinc-200">
            <button
              onClick={handleUndoComplete}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 py-3 bg-orange-100 text-orange-700 rounded-xl font-medium hover:bg-orange-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} />
              {isProcessing ? 'Processing...' : 'Undo Complete'}
            </button>
            <p className="text-xs text-center text-zinc-500 mt-2">This will delete the invoice (if unpaid) and remove the charge from customer balance</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailModal;
